import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["monthSelect", "yearSelect"]
  static values = {
    cancellationPeriod: Number,
    now: String // this is our date (we need it injected from rails for testing))
  }

  connect() {
    this.update()
  }

  update(event) {
    switch (this.subscriptionType()) {
    case "yearly":
      this.applyYearly()
      break;
    case "monthly":
      this.applyMonthly()
      break;
    default:
      this.applyNone()
    }
  }

  // ---------------------------
  // Detect selected subscription
  // ---------------------------
  subscriptionType() {
    return this.element.querySelector('#subscription_subscription_type')?.value
  }

  // ---------------------------
  // NONE: set date select to disabled
  // ---------------------------
  applyNone() {
    this.monthSelectTarget.disabled = true
    this.yearSelectTarget.disabled  = true
  }

  // ---------------------------
  // YEARLY: force December only
  // ---------------------------
  applyYearly() {
    this.yearSelectTarget.disabled  = false
    this.monthSelectTarget.disabled = false

    const now = new Date(this.nowValue)
    const currentYear = now.getFullYear()
    // after 15 december
    const isLateCutoff = now.getMonth() === 11 && now.getDate() > 15

    const minimumYear = isLateCutoff ? currentYear + 1 : currentYear
    const selectedYear = parseInt(this.yearSelectTarget.value, 10)

    if (selectedYear < minimumYear) {
      this.yearSelectTarget.value = String(minimumYear)
    }

    this.lockMonthOptionsForYearly()

    this.lockYearIfNeeded(isLateCutoff, currentYear)
  }

  lockMonthOptionsForYearly() {
    Array.from(this.monthSelectTarget.options).forEach(opt => {
      opt.disabled = opt.value !== "12"
    })
    this.monthSelectTarget.value = "12"
  }

  lockYearIfNeeded(isLateCutoff, currentYear) {
    Array.from(this.yearSelectTarget.options).forEach(opt => {
      if (isLateCutoff) {
	opt.disabled = parseInt(opt.value, 10) === currentYear
      } else {
	opt.disabled = false
      }
    })
  }

  // ---------------------------
  // MONTHLY: dynamic valid months
  // ---------------------------
  applyMonthly() {
    this.yearSelectTarget.disabled  = false
    this.monthSelectTarget.disabled = false

    const now = new Date(this.nowValue)
    const currentYear = now.getFullYear()

    this.unlockAllMonths()
    this.updateMonthAvailability()
    this.lockYearIfNeeded(false, currentYear)
  }

  // ---------------------------
  // CORE LOGIC
  // ---------------------------
  updateMonthAvailability() {
    const minDate = this.minimumAllowedDate()

    const selectedYear = parseInt(this.yearSelectTarget.value, 10)

    Array.from(this.monthSelectTarget.options).forEach(option => {
      const month = parseInt(option.value, 10)

      const candidate = new Date(selectedYear, month - 1, 1)

      option.disabled = candidate < minDate
    })

    this.ensureValidSelection(minDate)
  }

  ensureValidSelection(minDate) {
    const selected = this.selectedDate()

    if (selected < minDate) {
      this.yearSelectTarget.value = String(minDate.getFullYear())
      this.monthSelectTarget.value = String(minDate.getMonth() + 1)
    }
  }

  // ---------------------------
  // HELPERS
  // ---------------------------
  minimumAllowedDate() {
    const now = new Date(this.nowValue)

    const min = new Date(now)
    min.setDate(min.getDate() + this.cancellationPeriodDays())

    return new Date(min.getFullYear(), min.getMonth(), 1)
  }

  cancellationPeriodDays() {
    // Rails passes seconds (to_i), convert to days safely
    return this.cancellationPeriodValue / 86400
  }

  selectedDate() {
    const year = parseInt(this.yearSelectTarget.value, 10)
    const month = parseInt(this.monthSelectTarget.value, 10)

    return new Date(year, month - 1, 1)
  }

  unlockAllMonths() {
    Array.from(this.monthSelectTarget.options).forEach(opt => {
      opt.disabled = false
    })
  }
}
